import { styled } from "@pigment-css/react";
import { hasTrend, TrendPercent } from "@acto/taskpane/field-sales-2/components/primitives/TrendPercent";

const Figure = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[6],
  minWidth: 0,
}));

const LabelRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.fieldSalesTwo.spacing[8],
}));

export const Marker = styled("span")(({ theme }) => ({
  flexShrink: 0,
  width: 3,
  margin: `${theme.fieldSalesTwo.spacing[4]} 0`,
  borderRadius: 1,
}));

const LabelStack = styled("div")({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
});

const EmptyTrend = styled("p")(({ theme }) => ({
  margin: 0,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
}));

export const Label = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

export const Value = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["24"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "anywhere",
}));

const PreviousValue = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["18"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["130"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflowWrap: "anywhere",
}));

export interface KpiFigureContent {
  /** KPI name, e.g. "Auftragseingang". */
  label: string;
  /** Formatted current-period value. */
  value: string;
  /** Formatted comparison-period value. Omit when there is no comparison. */
  previousValue?: string;
  /** Percent delta vs the comparison value. */
  trendPercent: number | null;
}

export interface KpiFigureProps extends KpiFigureContent {
  seriesColor: string;
  reserveTrendRow?: boolean;
}

export function KpiFigure({
  label,
  value,
  previousValue,
  trendPercent,
  seriesColor,
  reserveTrendRow = false,
}: KpiFigureProps) {
  return (
    <Figure>
      <LabelRow>
        <Marker aria-hidden style={{ background: seriesColor }} />
        <LabelStack>
          {hasTrend(trendPercent) ? (
            <TrendPercent percent={trendPercent} size="label" />
          ) : (
            reserveTrendRow && <EmptyTrend aria-hidden>{"\u00a0"}</EmptyTrend>
          )}
          <Label>{label}</Label>
        </LabelStack>
      </LabelRow>
      <div>
        <Value>{value}</Value>
        {previousValue !== undefined && <PreviousValue>{previousValue}</PreviousValue>}
      </div>
    </Figure>
  );
}
